// Overview v3 display helpers: date-only values and the plain-language renewal badge (decision D5).
import type { TrackPaSummary } from '../../../dtos/patientmr/certificationPeriod.dto';

/** 'YYYY-MM-DD' → 'Nov 27' (or 'Nov 27, 2026' with the year). Date-only, so no timezone shift. */
export function formatDay(day: string | null | undefined, withYear = false): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}/.test(day)) return '—';
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC', ...(withYear ? { year: 'numeric' } : {}) });
}

export type BadgeTone = 'grey' | 'amber' | 'red' | 'green';

export const TONE_CLASSES: Record<BadgeTone, string> = {
  grey:  'bg-slate-100 text-slate-600',
  amber: 'bg-amber-100 text-amber-800',
  red:   'bg-red-100 text-red-700',
  green: 'bg-emerald-100 text-emerald-700',
};

/**
 * Renewal badge for a PA. The rules are fixed (window opens 30 days before the PA end date,
 * renewal is late 7 days before it); nobody configures them in the app.
 */
export function renewalBadge(pa: TrackPaSummary | null | undefined): { text: string; tone: BadgeTone } | null {
  const r = pa?.renewal;
  if (!pa || !r) return null;
  switch (r.status) {
    case 'RENEWED': return { text: 'Renewal submitted', tone: 'green' };
    case 'BEFORE':  return { text: `Renewal opens ${formatDay(r.opensOn)}`, tone: 'grey' };
    case 'OPEN':    return { text: `Renew by ${formatDay(r.renewBy)}`, tone: 'amber' };
    case 'LATE':    return { text: 'Renewal late', tone: 'red' };
    case 'ENDED':   return { text: `PA ended ${formatDay(pa.endDate)}`, tone: 'red' };
    default:        return null;
  }
}

const UNIT_WORD: Record<string, string> = { units: 'units', visits: 'visits', hours: 'hours' };

/** '350 of 400 units used' (or the status when nothing is approved yet). */
export function paUsageText(pa: TrackPaSummary): string {
  if (pa.approvedUnits === null || pa.usedUnits === null) {
    return pa.status === 'SUBMITTED' || pa.status === 'PENDED' ? 'Submitted, waiting for approval' : pa.status.toLowerCase();
  }
  const unit = UNIT_WORD[String(pa.unitType || '').toLowerCase()] || 'units';
  return `${pa.usedUnits} of ${pa.approvedUnits} ${unit} used`;
}
